import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { ApiResponse } from 'apisauce';
import RcInstanceDTO from '@robocorp/robocloud-internal-cds-sdk/lib/sdk/Types';
import { OkAction, ErrorAction } from './baseActions';
import { personalApiKeyService } from '../services/userAPI';
import { formatApiErrorPayload } from './utils';

type AppState = import('~/store').AppState;

export enum ApiKeyActionType {
  SILENT_LIST_PERSONAL_API_KEY = 'SILENT_LIST_PERSONAL_API_KEY',
  LIST_PERSONAL_API_KEYS = 'LIST_PERSONAL_API_KEYS',
  LIST_PERSONAL_API_KEYS_OK = 'LIST_PERSONAL_API_KEYS_OK',
  LIST_PERSONAL_API_KEYS_ERROR = 'LIST_PERSONAL_API_KEYS_ERROR',
  GET_PERSONAL_API_KEY = 'GET_PERSONAL_API_KEY',
  GET_PERSONAL_API_KEY_OK = 'GET_PERSONAL_API_KEY_OK',
  GET_PERSONAL_API_KEY_ERROR = 'GET_PERSONAL_API_KEY_ERROR',
  CREATE_PERSONAL_API_KEY = 'CREATE_PERSONAL_API_KEY',
  CREATE_PERSONAL_API_KEY_OK = 'CREATE_PERSONAL_API_KEY_OK',
  CREATE_PERSONAL_API_KEY_ERROR = 'CREATE_PERSONAL_API_KEY_ERROR',
  DELETE_PERSONAL_API_KEY = 'DELETE_PERSONAL_API_KEY',
  DELETE_PERSONAL_API_KEY_OK = 'DELETE_PERSONAL_API_KEY_OK',
  DELETE_PERSONAL_API_KEY_ERROR = 'DELETE_PERSONAL_API_KEY_ERROR',
  EDIT_PERSONAL_API_KEY = 'EDIT_PERSONAL_API_KEY',
  EDIT_PERSONAL_API_KEY_OK = 'EDIT_PERSONAL_API_KEY_OK',
  EDIT_PERSONAL_API_KEY_ERROR = 'EDIT_PERSONAL_API_KEY_ERROR',
}

type WithId = { id: string };

type GetPersonalApiKeyListAction =
  | Action<ApiKeyActionType.SILENT_LIST_PERSONAL_API_KEY>
  | Action<ApiKeyActionType.LIST_PERSONAL_API_KEYS>
  | OkAction<ApiKeyActionType.LIST_PERSONAL_API_KEYS_OK, RcInstanceDTO.AccessCredentials[]>
  | ErrorAction<ApiKeyActionType.LIST_PERSONAL_API_KEYS_ERROR>;

type GetPersonalApiKeyAction =
  | (Action<ApiKeyActionType.GET_PERSONAL_API_KEY> & WithId)
  | (OkAction<ApiKeyActionType.GET_PERSONAL_API_KEY_OK, RcInstanceDTO.AccessCredentialsWithSecret> &
      WithId)
  | (ErrorAction<ApiKeyActionType.GET_PERSONAL_API_KEY_ERROR> & WithId);

type CreatePersonalApiKeyAction =
  | Action<ApiKeyActionType.CREATE_PERSONAL_API_KEY>
  | OkAction<ApiKeyActionType.CREATE_PERSONAL_API_KEY_OK, RcInstanceDTO.AccessCredentials>
  | ErrorAction<ApiKeyActionType.CREATE_PERSONAL_API_KEY_ERROR>;

type DeletePersonalApiKeyAction =
  | (Action<ApiKeyActionType.DELETE_PERSONAL_API_KEY> & WithId)
  | (OkAction<ApiKeyActionType.DELETE_PERSONAL_API_KEY_OK, RcInstanceDTO.AccessCredentials> &
      WithId)
  | (ErrorAction<ApiKeyActionType.DELETE_PERSONAL_API_KEY_ERROR> & WithId);

type EditPersonalApiKeyAction =
  | (Action<ApiKeyActionType.EDIT_PERSONAL_API_KEY> & WithId)
  | (OkAction<ApiKeyActionType.EDIT_PERSONAL_API_KEY_OK, RcInstanceDTO.AccessCredentials> & WithId)
  | (ErrorAction<ApiKeyActionType.EDIT_PERSONAL_API_KEY_ERROR> & WithId);

export type ApiKeyAction =
  | GetPersonalApiKeyListAction
  | GetPersonalApiKeyAction
  | CreatePersonalApiKeyAction
  | DeletePersonalApiKeyAction
  | EditPersonalApiKeyAction;

export const getKeys =
  (): ThunkAction<void, AppState, null, GetPersonalApiKeyListAction> =>
  async (dispatch, getState) => {
    if (getState().userResources.personalApiKeys.allIds.length > 0) {
      dispatch({ type: ApiKeyActionType.SILENT_LIST_PERSONAL_API_KEY });
    } else {
      dispatch({ type: ApiKeyActionType.LIST_PERSONAL_API_KEYS });
    }

    const response = await personalApiKeyService.getApiKeys();
    if (response.ok) {
      dispatch({
        type: ApiKeyActionType.LIST_PERSONAL_API_KEYS_OK,
        data: response.data as RcInstanceDTO.AccessCredentials[],
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ApiKeyActionType.LIST_PERSONAL_API_KEYS_ERROR,
      });
    }
    return response;
  };

export const createApiKey =
  (name: string): ThunkAction<void, AppState, null, CreatePersonalApiKeyAction> =>
  async (dispatch) => {
    dispatch({ type: ApiKeyActionType.CREATE_PERSONAL_API_KEY });

    const response = await personalApiKeyService.createApiKey(name);
    if (response.ok) {
      dispatch({
        type: ApiKeyActionType.CREATE_PERSONAL_API_KEY_OK,
        data: response.data as RcInstanceDTO.AccessCredentials,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ApiKeyActionType.CREATE_PERSONAL_API_KEY_ERROR,
      });
    }
  };

export const deleteApiKeyById =
  (
    id: string,
  ): ThunkAction<
    Promise<ApiResponse<RcInstanceDTO.AccessCredentials>>,
    AppState,
    null,
    DeletePersonalApiKeyAction
  > =>
  async (dispatch) => {
    dispatch({ type: ApiKeyActionType.DELETE_PERSONAL_API_KEY, id });

    const response = await personalApiKeyService.deleteApiKeyById(id);
    if (response.ok && response.data) {
      dispatch({
        id,
        type: ApiKeyActionType.DELETE_PERSONAL_API_KEY_OK,
        data: response.data,
      });
    } else {
      dispatch({
        ...formatApiErrorPayload(response),
        type: ApiKeyActionType.DELETE_PERSONAL_API_KEY_ERROR,
        id,
      });
    }

    return response;
  };

export const getApiKeyById =
  (id: string): ThunkAction<Promise<void>, AppState, null, GetPersonalApiKeyAction> =>
  async (dispatch) => {
    dispatch({ type: ApiKeyActionType.GET_PERSONAL_API_KEY, id });
    const response = await personalApiKeyService.getApiKeyById(id);
    if (response.ok && response.data && response.data.secret) {
      dispatch({
        type: ApiKeyActionType.GET_PERSONAL_API_KEY_OK,
        data: response.data,
        id,
      });
    } else {
      dispatch({
        type: ApiKeyActionType.GET_PERSONAL_API_KEY_ERROR,
        error: response.problem ?? 'UNKNOWN_ERROR',
        id,
      });
    }
  };

export const editApiKey =
  (
    id: string,
    update: Partial<RcInstanceDTO.AccessCredentials>,
  ): ThunkAction<void, AppState, null, EditPersonalApiKeyAction> =>
  async (dispatch, getState) => {
    const key = getState().userResources.personalApiKeys.byId[id];
    const updated = { ...key, ...update };
    dispatch({
      type: ApiKeyActionType.EDIT_PERSONAL_API_KEY,
      id,
    });

    const response = await personalApiKeyService.editApiKey(id, updated);
    if (response.ok && response.data) {
      dispatch({
        id,
        type: ApiKeyActionType.EDIT_PERSONAL_API_KEY_OK,
        data: response.data,
      });
    } else {
      dispatch({
        id,
        type: ApiKeyActionType.EDIT_PERSONAL_API_KEY_ERROR,
        error: response.problem ?? 'UNKNOWN_ERROR',
      });
    }
  };
